import { Action, Mutation } from 'vuex-module-decorators';
import { InjectModule, InjectVuexModule } from 'vue-di/vuex';
import { Resolve } from 'vue-di';
import moment from 'moment';
import { EdgeSignatureApi } from '@trialcard/apigateway.client';
import { Field } from '@trialcard/apigateway.models';
import { AxiosInstance, AxiosRequestConfig } from 'axios';
import { CommonTools } from '../components/tools/commonTools';
import PathModule from './path';
import LayoutModule from './layout';
import EnrollmentModule from './enrollment';
import { Settings } from '~/services/settings';

@InjectModule({ stateFactory: true }, module)
export default class DocusignModule extends InjectVuexModule {
    @Resolve
    settings!: Settings;
    @Resolve
    pathModule!: PathModule
    @Resolve
    layout!: LayoutModule;
    @Resolve
    enrollment!: EnrollmentModule;
    @Resolve
    edgeSignature!: EdgeSignatureApi;

    envelopeId = '';
    recipientViewURL = '';
    eventType: string | null = null;

    selectTemplate() {
        const language = this.layout.isSpanish ? 'Spanish' : 'English';
        const { path } = this.pathModule;
        const templateId = this.settings[`${path}TemplateId${language}` as keyof Settings]
        return templateId as string;
    }

    selectBrandId() {
        const language = this.layout.isSpanish ? 'Spanish' : 'English';
        const brandId = this.settings[`brandId${language}` as keyof Settings];
        return brandId as string;
    }

    structureCreateEnvelopeRequest() {
        const {
            dateOfBirth,
            emailAddresses,
            phoneNumbers,
        } = this.enrollment.patientInformationData
        const { path } = this.pathModule;
        const firstName = CommonTools.titleCase(this.enrollment.patientInformationData.firstName || '');
        const lastName = CommonTools.titleCase(this.enrollment.patientInformationData.lastName || '');
        const patEmail = emailAddresses.length > 0 && emailAddresses[0].address || '';
        const repEmail = this.enrollment.repInformation.representativeEmail;
        const phone = phoneNumbers.length > 0 && phoneNumbers[0].phoneNumber || '';
        const patName = CommonTools.titleCase(`${firstName} ${lastName}`);
        const representative = this.enrollment.contacts.length > 0 && this.enrollment.contacts[0] || {};
        const repName = CommonTools.titleCase(`${representative.firstName} ${representative.lastName}`);
        const zipCode = this.enrollment.patientInformationData.addresses.length > 0 && this.enrollment.patientInformationData.addresses[0].zip || '';
        const request = {
            programId: +this.settings.programId,
            documentType: 'SRF', // Hard coded for now based on documentation from Team Code Brigade
            brandId: this.selectBrandId(),
            senderId: this.settings.senderId,
            templateId:  this.selectTemplate(),
            replyEmailAddressOverride: 'no-reply@docusign.net',
            expireInDays: +this.settings.expireInDays,
            emailSubject: ' ',
            emailBody: ' ',
            send: true, // Send needs to be set to true to request a RecipientView.
            embedded: true, // Embedded needs to be set to true for envelope intended for RecipientView
            externalProgramCode: this.settings.externalProgramCode,
            externalPartyCode:  this.settings.externalPartyCode,
            recipients: [
                {
                    name: path === 'representative' ? repName : patName,
                    email: path === 'representative' ? repEmail : patEmail,
                    role: path.charAt(0).toUpperCase() + path.slice(1).toLowerCase(),
                }
            ],
            fields: [
                {
                    name: 'Pat_Full_Name',
                    value: patName
                },
                {
                    name: 'Pat_First',
                    value : firstName
                },
                {
                    name:'Pat_Last',
                    value : lastName
                },
                {
                    name:'Pat_DOB',
                    value : moment(dateOfBirth).format('L')
                },
                {
                    name:'Pat_Email',
                    value : patEmail
                },
                {
                    name:'Pat_Primary_Ph',
                    value : phone
                },
                {
                    name:'Pat_ZIP',
                    value : zipCode
                },
            ] as Field[]
        };
        if (path === 'representative') {
            // Remove patient email in lieu of rep email, add representative fields
            request.fields = [
                ...request.fields.filter(field => field.name !== 'Pat_Email'),
                {
                    name:'Rep_Name',
                    value : repName
                },
                {
                    name:'Rep_Relation',
                    value : this.enrollment.repInformation.relationshipToPatient
                },
                {
                    name:'Rep_Email',
                    value : repEmail
                }
            ]
        }
        return request;
    }

    structureRecipientViewUrlRequest() {
        const { path } = this.pathModule;
        let email = '';
        let name = '';
        const { firstName, lastName, emailAddresses } = this.enrollment.patientInformationData;
        if (path === 'representative') {
            const representative = this.enrollment.contacts.length > 0 && this.enrollment.contacts[0] || {};
            email = this.enrollment.repInformation.representativeEmail;
            name = CommonTools.titleCase(`${representative.firstName} ${representative.lastName}`);
        } else {
            email = emailAddresses.length > 0 && emailAddresses[0].address || '';
            name = CommonTools.titleCase(`${firstName} ${lastName}`);
        }

        const request = {
            programId: +this.settings.programId,
            envelopeId: this.envelopeId,
            email,
            name,
            returnUrl:  this.layout.isSpanish ? `${CommonTools.getBaseUrl(window.location.href, this.settings)}success?language=spanish`: `${CommonTools.getBaseUrl(window.location.href, this.settings)}success?language=english`,
        };
        return request;
    }

    @Action({})
    async createEnvelope() {
        let isSuccessful = false;
        const request = this.structureCreateEnvelopeRequest();
        try {
            const axios: AxiosInstance = (this.edgeSignature as any).axios;
            const axiosRequest: AxiosRequestConfig = {
                url: 'edge/signature/v1/EmailSignatureTemplate/CreateEnvelope',
                method: 'POST',
                data: request,
                headers: {
                    'x-program-id': this.settings.programId!,
                },
            };
            await Promise.all([
                (this.edgeSignature as any).authentications.Token.applyToRequest(axiosRequest),
            ]);
            const { data } = await axios.request(axiosRequest);
            const envelopeId = data && data.data && data.data.envelopeId || '';
            if (!envelopeId) throw new Error('Envelope creation failed')
            this.setEnvelopeId(envelopeId);
            isSuccessful = data.data && data.success;
        } catch(error) {
            console.error('Error from creating Docusign envelope:',error);
        }
        return isSuccessful
    }

    @Action({})
    async requestURL() {
        let isSuccessful = false;
        const request = this.structureRecipientViewUrlRequest();
        try {
            const response = (await this.edgeSignature.recipientViewRequestUrl(request)).data;
            const recipientViewURL = response && response.data && response.data.url || '';
            if (!recipientViewURL) throw new Error('Getting URL failed')
            await this.setRecipientViewURL(recipientViewURL);
            isSuccessful = true;
        } catch(error) {
            console.error('Error from requesting recipient view URL:',error);
        }
        return isSuccessful;
    }

    @Mutation
    setEnvelopeId(envelopeId: string) {
        this.envelopeId = envelopeId;
    }

    @Mutation
    setRecipientViewURL(recipientViewURL: string) {
        this.recipientViewURL = recipientViewURL;
    }

    @Mutation
    setEventType(eventType: string | null) {
        this.eventType = eventType;
    }

}
